import { useMemo } from 'react';
import { LwdFeatureSeats } from '@/api';
import { useDevices } from '../providers';
import { useCompanyPermissions } from '@/companies/hooks';
import { useFetchSubscriptionUsage } from '@/companies/queries';
import {
    isFallEffectivelyEnabled,
    isTiltEffectivelyEnabled
} from '../util/device-alert-usage';
import { deriveDeviceFeatureStates } from '../util/device-feature-states';

type AlertLicenseAvailability = {
    /** The total number of addon slots for this feature. */
    limit: number;
    /** Whether a new device can still enable this feature. */
    canEnableNew: boolean;
};

/**
 * Determines whether the company has spare fall/tilt/geofence addon licenses to
 * enable the feature on a new device. Counts come from the authoritative
 * server-side usage endpoint (`used` is the company-wide effectively-enabled
 * count) rather than from devices in memory, which a team admin only partially
 * holds. The device being edited is excluded from `used` so it can always be
 * turned off / kept on. `loading` is true until usage has resolved; callers
 * should treat features as not-enableable until then.
 */
export function useDeviceAlertLicenses(params: {
    companyId: string;
    deviceId: string;
}): {
    fall: AlertLicenseAvailability;
    tilt: AlertLicenseAvailability;
    geo: AlertLicenseAvailability;
    loading: boolean;
} {
    const { companyId, deviceId } = params;
    const { devices } = useDevices(companyId);
    const { canEditAnyDevice } = useCompanyPermissions();
    const { usage, status } = useFetchSubscriptionUsage(companyId, {
        enabled: canEditAnyDevice
    });

    return useMemo(() => {
        const loading = status !== 'success';
        const device = devices.find((d) => d.id === deviceId);

        // The edited device already counts toward the server's `used` when it
        // has the feature on, so subtract it: it can always keep / re-enable.
        function availability(
            seats: LwdFeatureSeats | undefined,
            enabledOnThisDevice: boolean
        ): AlertLicenseAvailability {
            const total = seats?.total ?? 0;
            const usedByOthers =
                (seats?.used ?? 0) - (enabledOnThisDevice ? 1 : 0);
            return {
                limit: total,
                canEnableNew: usedByOthers < total
            };
        }

        return {
            fall: availability(
                usage?.lwd?.fall,
                device ? isFallEffectivelyEnabled(device) : false
            ),
            tilt: availability(
                usage?.lwd?.tilt,
                device ? isTiltEffectivelyEnabled(device) : false
            ),
            geo: availability(
                usage?.lwd?.geofence,
                device
                    ? deriveDeviceFeatureStates(device).geofence.enabled
                    : false
            ),
            loading
        };
    }, [devices, deviceId, usage, status]);
}
